import { type Field, match as matchField } from "foldkit/fieldValidation";
import type { Html, HtmlBuilder } from "foldkit/html";

/**
 * Record form limits, shared by the validation rules and the inputs' `maxlength`.
 * The server caps name/subtitle at 256 and description at 10,000 (core/records.ts);
 * the client keeps v1's stricter 5,000 for description.
 */
export const RECORD_FIELD_MAX = {
	name: 256,
	subtitle: 256,
	email: 256,
	description: 5000,
} as const;

export const isInvalid = (field: Field<string>): boolean =>
	field._tag === "Invalid";

export const error = <Message>(
	h: HtmlBuilder<Message>,
	field: Field<string>,
): Html =>
	matchField(field, {
		onNotValidated: () => h.empty,
		onValidating: () => h.empty,
		onValid: () => h.empty,
		onInvalid: ({ errors }) =>
			h.div(
				[h.Class("mt-1")],
				errors.map((message) =>
					h.p([h.Class("text-sm text-error")], [message]),
				),
			),
	});

export const textInput = <Message>(
	h: HtmlBuilder<Message>,
	id: string,
	label: string,
	field: Field<string>,
	toMessage: (value: string) => Message,
	type = "text",
	placeholder?: string,
	maxLength?: number,
): Html =>
	h.div(
		[],
		[
			h.label([h.For(id), h.Class("block font-semibold")], [label]),
			h.input([
				h.Id(id),
				h.Type(type),
				...(placeholder === undefined ? [] : [h.Placeholder(placeholder)]),
				...(maxLength === undefined ? [] : [h.Maxlength(maxLength)]),
				h.Class(
					`input input-bordered w-full ${isInvalid(field) ? "input-error" : ""}`,
				),
				h.Value(field.value),
				h.OnInput(toMessage),
			]),
			error(h, field),
		],
	);
